import React, { ReactNode, useRef, useState, useEffect } from 'react';
import {
	Button,
	OverlayArrow,
	Tooltip as TooltipReactAria,
	TooltipTrigger,
	TooltipProps as TooltipReactAriaProps,
} from 'react-aria-components';
import InfoIcon from '@/assets/Icons/Info-Line.svg?react';
import styles from './Tooltip.module.scss';
import classNames from 'classnames';

interface TooltipProps {
	icon?: ReactNode;
	text: string;
	className?: string;
	triggerClassName?: string;
	triggerAriaLabel?: string;
	onTriggerClick?: () => void;
	showLearnMoreText?: boolean;
	learnMoreLabel?: string;
	onLearnMoreClick?: () => void;
	delay?: number;
	offset?: number;
	placement?: 'top' | 'bottom' | 'left' | 'right';
	isDisabled?: boolean;
	defaultOpen?: boolean;
}

interface MyTooltipProps extends Omit<TooltipReactAriaProps, 'children'> {
	children: React.ReactNode;
}

const MyTooltip = ({ children, ...props }: MyTooltipProps) => {
	return (
		<TooltipReactAria {...props}>
			<OverlayArrow className={styles.reactAriaOverlayArrow}>
				<svg width={10} height={10} viewBox="0 0 8 8">
					<path d="M0 0 L4 4 L8 0" />
				</svg>
			</OverlayArrow>
			{children}
		</TooltipReactAria>
	);
};

const Tooltip: React.FC<TooltipProps> = ({
	icon = <InfoIcon />,
	text,
	className,
	triggerClassName,
	triggerAriaLabel,
	onTriggerClick,
	showLearnMoreText = false,
	learnMoreLabel = 'Learn more',
	onLearnMoreClick,
	delay = 5.5,
	offset = 0,
	placement = 'top',
	isDisabled = false,
	defaultOpen = false,
}) => {
	const ref = useRef<HTMLButtonElement>(null);
	const [isTouchOpen, setIsTouchOpen] = useState(false);
	const triggerRef = useRef<HTMLDivElement>(null);

	const handleTouchStart = (e: React.TouchEvent) => {
		e.preventDefault();
		setIsTouchOpen(!isTouchOpen);
	};

	useEffect(() => {
		const handleTouchOutside = (event: TouchEvent) => {
			if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
				setIsTouchOpen(false);
			}
		};

		if (isTouchOpen) {
			document.addEventListener('touchstart', handleTouchOutside);
		}

		return () => {
			document.removeEventListener('touchstart', handleTouchOutside);
		};
	}, [isTouchOpen]);

	if (showLearnMoreText && onLearnMoreClick) {
		return (
			<button type="button" className={styles.iconWrapper} onClick={onLearnMoreClick}>
				{icon}
				<span className={styles.learnMoreText}>{learnMoreLabel}</span>
			</button>
		);
	}

	return (
		<div ref={triggerRef} onTouchStart={handleTouchStart}>
			<TooltipTrigger
				delay={delay}
				isDisabled={isDisabled}
				defaultOpen={defaultOpen}
				isOpen={isTouchOpen ? true : undefined}
			>
				<Button
					ref={ref}
					className={classNames(styles.iconWrapper, triggerClassName)}
					aria-label={triggerAriaLabel ?? text}
					onPress={onTriggerClick}
				>
					{icon}
					{showLearnMoreText && <span className={styles.learnMoreText}>{learnMoreLabel}</span>}
				</Button>
				<MyTooltip
					placement={placement}
					offset={offset}
					className={classNames(styles.reactAriaTooltip, className)}
				>
					{text}
				</MyTooltip>
			</TooltipTrigger>
		</div>
	);
};

export default Tooltip;
